/**
 * Nombre completo de un producto: categoria + marca + nombre, sin repetir lo que ya
 * esta dentro del nombre.
 *
 * El catalogo de EBM guarda muchos nombres que son solo la referencia del fabricante
 * («CM70007», «G274QPF»): sueltos no dicen que es. La pagina del producto ya armaba
 * «Headset KAPEYDESI CM70007» para el titulo, pero el WhatsApp, el carrito y la
 * disponibilidad mandaban solo «CM70007» y la tienda no sabia por que preguntaban.
 * Todo lo que el cliente o la tienda leen usa este nombre.
 */
export interface NamedProduct {
  name?: string | null;
  brand?: string | null;
  productSubCategory?: string | null;
  category?: string | null;
}

export function fullProductName(product: NamedProduct): string {
  const base = (product.name ?? '').trim();
  const baseLc = base.toLowerCase();
  const category = product.productSubCategory ?? product.category ?? '';
  const prefix = [category, product.brand]
    .map((part) => (part ?? '').trim())
    .filter((part) => part.length > 0)
    .filter((part) => !baseLc.includes(part.toLowerCase()));
  return [...prefix, base].join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * <title> de la ficha: marca + nombre primero y la categoria despues
 * («ASUS ROG Strix 5070 12GB – Tarjetas Gráficas (GPU) | SpartechRD Santo Domingo»).
 *
 * Con fullProductName el titulo empezaba por la categoria tal como la nombra EBM
 * («Tarjetas Gráficas (GPU) ASUS…»): nadie busca asi, y Google pesa mas las
 * primeras palabras y corta pasados ~60 caracteres. La categoria se queda porque
 * hay nombres que son solo la referencia («G274QPF») y sin ella el titulo no
 * diria que es un monitor. La misma regla vive en
 * scripts/lib/product-title.mjs; src/utils/productName.test.ts vigila
 * que no se separen.
 */
export function productPageTitle(product: NamedProduct): string {
  const base = (product.name ?? '').trim();
  const brand = (product.brand ?? '').trim();
  const head = (brand && !base.toLowerCase().includes(brand.toLowerCase()) ? `${brand} ${base}` : base)
    .replace(/\s+/g, ' ')
    .trim();
  const category = (product.productSubCategory ?? product.category ?? '').trim();
  const short = head.length > 62 ? `${head.slice(0, 62).trimEnd()}...` : head;
  const parts = [short, category && !head.toLowerCase().includes(category.toLowerCase()) ? category : '']
    .filter((part) => part.length > 0);
  return `${parts.join(' – ')} | SpartechRD Santo Domingo`;
}

/**
 * Enlace publico de la ficha del producto. Va en los mensajes de WhatsApp: el chat muestra
 * la vista previa (foto, nombre y precio) y la tienda sabe de inmediato cual es.
 */
export function productPageUrl(companyId: string | number, productId: number | string): string {
  return `https://spartechrd.com/products/${companyId}/${productId}`;
}
